import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CardsItemIcon, Field, FieldTypes, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import * as yup from 'yup';

import {
  DATE_MONTH_YEAR_FORMAT,
  DEFAULT_CARD_EXP_DATE_FORMAT,
  FIRST_DAY_NUMBER,
  INVALID_TEXT,
  REQUIRED_TEXT,
} from '@/common/constants';
import masks from '@/common/masks';
import regexp from '@/common/regexp';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';

const EXP_DATE_SECOND_PART_MONTH_REGEXP = '[0-2]';

export enum DebitCardFields {
  nameOnCard = 'nameOnCard',
  cardNumber = 'cardNumber',
  expirationDate = 'expirationDate',
  cardType = 'cardType',
}

export interface DebitCardPayload {
  [DebitCardFields.nameOnCard]: string;
  [DebitCardFields.cardNumber]: string;
  [DebitCardFields.expirationDate]: string;
  [DebitCardFields.cardType]: string;
}

export const DEBIT_CARD_TEST_ID_PREFIX = 'DebitCard';
const smallInputStyles = {
  style: {
    justifyContent: 'flex-start',
  },
  inputWrapCssClass: 'small-input',
};

export const useDebitCardFields = (values?: DebitCardPayload) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const editableMethod = useStore((store) => store.editableMethod);

  const defaultValues = useMemo(
    () => ({
      [DebitCardFields.nameOnCard]: values?.nameOnCard || '',
      [DebitCardFields.cardNumber]: values?.cardNumber || '',
      [DebitCardFields.expirationDate]: values?.expirationDate || '',
    }),
    [values]
  );
  const bankCardType = useMemo(
    () =>
      Object.keys(regexp.BANK_CARD).reduce(
        (adapter, type) => ({
          ...adapter,
          [type]: regexp.BANK_CARD[type as keyof typeof regexp.BANK_CARD].test(
            defaultValues[DebitCardFields.cardNumber].replace(regexp.SPACE_SYMBOL, '')
          ),
        }),
        {} as typeof regexp.BANK_CARD
      ),
    [defaultValues]
  );

  const fields = useMemo<Field[]>(
    () => [
      {
        name: DebitCardFields.nameOnCard,
        type: FieldTypes.Text,
        label: t('Name on card'),
        placeholder: t('Name on card'),
        testId: `${DEBIT_CARD_TEST_ID_PREFIX}_${DebitCardFields.nameOnCard}`,
        validator: yup.string().required(REQUIRED_TEXT),
        defaultValue: editableMethod?.requisites?.cardHolderName || '',
      },
      {
        name: DebitCardFields.cardNumber,
        type: FieldTypes.Mask,
        mask: masks.FULL_DEBIT_CARD,
        label: t('Card number'),
        placeholder: t('Card number'),
        testId: `${DEBIT_CARD_TEST_ID_PREFIX}_${DebitCardFields.cardNumber}`,
        defaultValue: editableMethod?.requisites?.cardLast4
          ? `000000000000${editableMethod?.requisites?.cardLast4}`
          : '',
        validator: yup
          .string()
          .test({
            test: (val) =>
              Object.values(regexp.BANK_CARD_FULL).some((exp) =>
                exp.test((val || '').replace(regexp.SPACE_SYMBOL, ''))
              ),
            message: t(INVALID_TEXT),
          })
          .required(t(REQUIRED_TEXT)),
        rightIcon: <CardsItemIcon color="iconAccent" width="23px" />,
      },
      {
        name: DebitCardFields.expirationDate,
        type: FieldTypes.Mask,
        mask: masks.DEFAULT_CARD_EXP_DATE,
        formatChars: {
          2: EXP_DATE_SECOND_PART_MONTH_REGEXP,
        },
        label: t('Expiration date'),
        placeholder: t('MM / YY'),
        testId: `${DEBIT_CARD_TEST_ID_PREFIX}_${DebitCardFields.expirationDate}`,
        defaultValue: editableMethod?.requisites?.cardExpirationDate
          ? dayjs(editableMethod?.requisites?.cardExpirationDate)
              .format(DATE_MONTH_YEAR_FORMAT)
              .replace('/', ' / ')
          : '',
        validator: yup
          .string()
          .test({
            test: (val) => {
              const month = parseInt((val || '').split(' / ')[0] || '0', 10);
              const year = parseInt((val || '').split(' / ')[1] || '0', 10);
              const valueDate = regexp.DEFAULT_CARD_EXP_DATE.test(val || '')
                ? dayjs(`${month}/${FIRST_DAY_NUMBER}/20${year}`, DEFAULT_CARD_EXP_DATE_FORMAT).toDate()
                : null;
              return valueDate
                ? dayjs().isBefore(valueDate.setMonth(valueDate?.getMonth() + 1), 'months')
                : false;
            },
            message: t(INVALID_TEXT),
          })
          .required(t(REQUIRED_TEXT)),
        ...(isMobile ? {} : smallInputStyles),
      },
      // {
      //   name: DebitCardFields.securityNumber,
      //   type: FieldTypes.Mask,
      //   mask: SECRET_CODE,
      //   label: t('Security number'),
      //   placeholder: t('XXX'),
      //   testId: `${DEBIT_CARD_TEST_ID_PREFIX}_${DebitCardFields.securityNumber}`,
      //   validator: yup.string()
      //     .required(REQUIRED_TEXT)
      //     .test({
      //       test: (val) => (bankCardType.VISA && val?.length === 3)
      //       || (bankCardType.MASTERCARD && val?.length === 3)
      //       || (bankCardType.AMEX && val?.length === 4),
      //       message: INVALID_TEXT,
      //     }),
      //   rightIcon: (
      //     <SecurityCardInfoPopup
      //       icon={(
      //         <>
      //           <Box margin={{ right: 'spacing8' }}>
      //             <SecurityCodeIcon1 />
      //           </Box>
      //           <SecurityCodeIcon2 />
      //         </>
      //       )}
      //       title="What is a Card Security code?"
      //       description="3-digit security code usually found on the back of your card. American Express cards have a 4-digit code located on the front."
      //     />
      //   ),
      //   ...smallInputStyles,
      // },
    ],
    [t, editableMethod, isMobile]
  );

  return { fields, bankCardType };
};
